import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { validate } from 'validate.js';
import PropTypes from 'prop-types';
import { makeStyles } from '@mui/styles';
import { SelectField } from 'components';
import { Checkbox, Grid, FormControlLabel, TextField } from '@mui/material';
import _ from 'underscore';
import DatePicker from 'components/DatePicker/DatePicker.js';

import { SetFormState, GetLocalDropdown, GetClusterDropdown, GetSiteNameDropdown } from '../../actions/StreetScapeTrackerActions';

const schema = {
	local: {
		presence: { allowEmpty: false, message: 'is required' },
	},
	cluster: {
		presence: { allowEmpty: false, message: 'is required' },
	},
	siteName: {
		presence: { allowEmpty: false, message: 'is required' },
	},
	activationDate: {
		presence: { allowEmpty: true, message: 'is required' },
	},
	activationStatus: {
		presence: { allowEmpty: false, message: 'is required' },
	},
	sow: {
		presence: { allowEmpty: false, message: 'is required' },
	},
	project: {
		presence: { allowEmpty: false, message: 'is required' },
	},
	vendor: {
		presence: { allowEmpty: false, message: 'is required' },
	},
	fieldPartner: {
		presence: { allowEmpty: false, message: 'is required' },
	}
};

const useStyles = makeStyles((theme) => ({
	root: {},
	fields: {
		margin: theme.spacing(-1),
		display: 'flex',
		flexWrap: 'wrap',
		'& > *': {
			flexGrow: 1,
			margin: theme.spacing(1),
		},
	},
	submitButton: {
		marginTop: theme.spacing(2),
		width: '100%',
	},
	divider: {
		marginTop: theme.spacing(2),
		marginBottom: theme.spacing(2),
	},
	noBorder: {
		border: 'none',
	}
}));

const StreetScapeTrackerDialogFormCreate = (props) => {
	const { ...rest } = props;
	const classes = useStyles();
	const dispatch = useDispatch();
	const setFormState = formState => dispatch(SetFormState(formState));
	const formState = useSelector((state) => state.streetScapeTrackerReducer.formState);
	const siteLocalDropdown = useSelector((state) => state.streetScapeTrackerReducer.siteLocalDropdown);
	const siteClusterDropdown = useSelector((state) => state.streetScapeTrackerReducer.siteClusterDropdown);
	const siteNameDropdown = useSelector((state) => state.streetScapeTrackerReducer.siteNameDropdown);

	const _hasError = (field) => (!!(formState.touched[field] && formState.errors[field]));

	const _handleChange = (event) => {
		setFormState({
			...formState,
			values: {
				...formState.values,
				[event.target.name]: event.target.value,
			},
			touched: {
				...formState.touched,
				[event.target.name]: true,
			},
		});
	};

	const _dateChange = (value) => {
		setFormState({
			...formState,
			values: {
				...formState.values,
				activationDate: value,
			},
			touched: {
				...formState.touched,
				activationDate: true,
			},
		});
	};

	useEffect(() => {
		dispatch(GetLocalDropdown());
		setFormState({
			...formState,
			values: {
				...formState.values,
				activationStatus: 'Scheduled',
				project: 'StreetScape',
				vendor: 'Ericsson',
				fieldPartner: 'Boldyn',
				sow: 'L18 + L21 + NR36',
			},
			touched: {
				...formState.touched,
				activationStatus: true,
			},
		});
	}, []);

	useEffect(() => {
		dispatch(GetClusterDropdown(formState.values.local));
	}, [formState.values.local]);

	useEffect(() => {
		dispatch(GetSiteNameDropdown(formState.values.cluster));
	}, [formState.values.local, formState.values.cluster]);

	useMemo(() => {
		const newTouched = {};

		if (formState.submit) {
			_.each(_.keys(schema), item => {
				newTouched[item] = true;
			});
		}

		const errors = validate(formState.values, schema);

		setFormState({
			...formState,
			isValid: !errors,
			errors: errors || {},
			touched: {
				...formState.touched,
				...newTouched
			}
		});
	}, [formState.values, formState.submit]);

	return (
		<div className={classes.fields}>
			<Grid container spacing={2}>
				<Grid item md={2} xs={2}>
					<SelectField
						id="local"
						name="local"
						hintText="Local"
						variant="outlined"
						defaultValue=""
						menuItems={siteLocalDropdown || []}
						value={formState.values.local || ''}
						onChange={_handleChange}
						error={_hasError('local')}
						helperText={
							_hasError('local')
								? formState.errors.local[0]
								: null
						}
					/>
				</Grid>
				<Grid item md={2} xs={2}>
					<SelectField
						id="cluster"
						name="cluster"
						hintText="Cluster"
						variant="outlined"
						defaultValue=""
						menuItems={siteClusterDropdown || []}
						value={formState.values.cluster || ''}
						onChange={_handleChange}
						error={_hasError('cluster')}
						helperText={
							_hasError('cluster')
								? formState.errors.cluster[0]
								: null
						}
						disabled={!formState.values.local}
					/>
				</Grid>
				<Grid item md={2} xs={2}>
					<SelectField
						id="siteName"
						name="siteName"
						hintText="Site Name"
						variant="outlined"
						defaultValue=""
						menuItems={siteNameDropdown || []}
						value={formState.values.siteName || ''}
						onChange={_handleChange}
						error={_hasError('siteName')}
						helperText={
							_hasError('siteName')
								? formState.errors.siteName[0]
								: null
						}
						disabled={!formState.values.cluster}
					/>
				</Grid>
				<Grid item md={4} xs={4}>
					<DatePicker
						id="activationDate"
						label="Activation Date"
						error={_hasError('activationDate')}
						inputFormat="YYYY-MM-DD"
						value={formState.values.activationDate}
						onChange={(value) => _dateChange(value)}
					/>
				</Grid>
			</Grid>
			<Grid container spacing={2}>
				<Grid item md={2} xs={2}>
					<TextField
						id="activationStatus"
						name="activationStatus"
						label="Activation Status"
						variant="outlined"
						value={formState.values.activationStatus || ''}
						onChange={_handleChange}
						disabled
					/>
				</Grid>
				<Grid item md={2} xs={2}>
					<TextField
						id="sow"
						name="sow"
						label="SOW"
						variant="outlined"
						value={formState.values.sow || ''}
						onChange={_handleChange}
						disabled
					/>
				</Grid>
				{/* New row */}
				<Grid item md={2} xs={2}>
					<TextField
						id="project"
						name="project"
						label="Project"
						variant="outlined"
						value={formState.values.project || ''}
						onChange={_handleChange}
						disabled
					/>
				</Grid>
				<Grid item md={2} xs={2}>
					<TextField
						id="vendor"
						name="vendor"
						label="Vendor"
						variant="outlined"
						value={formState.values.vendor || ''}
						onChange={_handleChange}
						disabled
					/>
				</Grid>
				<Grid item md={2} xs={2}>
					<TextField
						id="fieldPartner"
						name="fieldPartner"
						label="Field Partner"
						variant="outlined"
						value={formState.values.fieldPartner || ''}
						onChange={_handleChange}
						disabled
					/>
				</Grid>
			</Grid>
		</div>
	);
};

export default StreetScapeTrackerDialogFormCreate;
